/**
 * useEntityListState — generic URL-synced list state.
 *
 * A configurable counterpart to useItemListState that works for any entity
 * type (Cycles, Tracks, Sessions, etc.).
 *
 * URL params: q (search), sort (field:dir), group (field), view (table)
 * localStorage: density (per storageKey)
 * Local state: collapsed groups (groups are OPEN by default), row selection,
 *              active filters (field → string[])
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// ─── Public types ─────────────────────────────────────────────────────────────

export type DensityMode = 'compact' | 'default' | 'comfortable';

export interface SortSpec {
  field: string;
  direction: 'asc' | 'desc';
}

export interface EntityColumnDef {
  key: string;
  label: string;
  defaultVisible: boolean;
}

export interface EntityFilterField {
  key: string;
  label: string;
  options: { value: string; label: string }[];
}

export interface EntityListStateConfig {
  allColumns: EntityColumnDef[];
  /** Default sort — pass a MODULE-LEVEL constant, not an inline object, for stability. */
  defaultSortField?: string;
  defaultSortDirection?: 'asc' | 'desc';
  storageKey: string;
  /** Filter fields for the filter panel. */
  filterFields?: EntityFilterField[];
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function getStoredDensity(key: string): DensityMode {
  try {
    const v = localStorage.getItem(`entityListDensity_${key}`);
    if (v === 'compact' || v === 'default' || v === 'comfortable') return v;
  } catch {}
  return 'default';
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useEntityListState(config: EntityListStateConfig) {
  const {
    allColumns,
    defaultSortField = 'updatedAt',
    defaultSortDirection = 'desc',
    storageKey,
    filterFields = [],
  } = config;

  // Keep primitives stable across renders
  const defaultFieldRef = useRef(defaultSortField);
  const defaultDirRef = useRef(defaultSortDirection);

  const [searchParams, setSearchParams] = useSearchParams();

  // ── Search ──────────────────────────────────────────────────────────────────
  const searchQuery = searchParams.get('q') || '';
  const setSearchQuery = useCallback(
    (q: string) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (q) next.set('q', q); else next.delete('q');
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  // ── Sort ────────────────────────────────────────────────────────────────────
  const sort: SortSpec = useMemo(() => {
    const raw = searchParams.get('sort');
    if (!raw) return { field: defaultFieldRef.current, direction: defaultDirRef.current };
    const [field, dir] = raw.split(':');
    return { field, direction: dir === 'asc' ? 'asc' : 'desc' };
  }, [searchParams]);

  const setSort = useCallback(
    (spec: SortSpec) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (spec.field === defaultFieldRef.current && spec.direction === defaultDirRef.current) {
          next.delete('sort');
        } else {
          next.set('sort', `${spec.field}:${spec.direction}`);
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const toggleSort = useCallback(
    (field: string) => {
      setSort({
        field,
        direction: sort.field === field && sort.direction === 'desc' ? 'asc' : 'desc',
      });
    },
    [sort, setSort],
  );

  // ── Group ───────────────────────────────────────────────────────────────────
  const groupBy = searchParams.get('group') || null;
  const setGroupBy = useCallback(
    (field: string | null) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (field) next.set('group', field); else next.delete('group');
        return next;
      }, { replace: true });
      // Reset collapsed groups whenever grouping changes so everything is open
      setCollapsedGroups(new Set());
    },
    [setSearchParams],
  );

  // ── Collapsed groups (groups are OPEN by default) ───────────────────────────
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const isGroupOpen = useCallback((key: string) => !collapsedGroups.has(key), [collapsedGroups]);
  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // ── Active filters ──────────────────────────────────────────────────────────
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});

  const setFieldFilter = useCallback((field: string, values: string[]) => {
    setActiveFilters(prev => {
      if (values.length === 0) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: values };
    });
  }, []);

  const clearAllFilters = useCallback(() => setActiveFilters({}), []);

  const filterCount = useMemo(
    () => Object.values(activeFilters).filter(v => v.length > 0).length,
    [activeFilters],
  );

  /** Apply active filters to an array of items using a field-accessor function. */
  const applyFilters = useCallback(
    <T>(items: T[], getField: (item: T, field: string) => string | undefined): T[] => {
      const entries = Object.entries(activeFilters).filter(([, values]) => values.length > 0);
      if (entries.length === 0) return items;
      return items.filter(item =>
        entries.every(([field, values]) => {
          const v = getField(item, field) ?? '';
          return values.includes(v);
        }),
      );
    },
    [activeFilters],
  );

  // ── Density ─────────────────────────────────────────────────────────────────
  const [density, setDensityInternal] = useState<DensityMode>(() => getStoredDensity(storageKey));
  const setDensity = useCallback((mode: DensityMode) => {
    setDensityInternal(mode);
    try { localStorage.setItem(`entityListDensity_${storageKey}`, mode); } catch {}
  }, [storageKey]);

  // ── Column visibility ───────────────────────────────────────────────────────
  const defaultVisible = useMemo(
    () => allColumns.filter(c => c.defaultVisible).map(c => c.key),
    [allColumns],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(defaultVisible);

  const toggleColumn = useCallback((key: string) => {
    setVisibleColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
    );
  }, []);

  const reorderColumns = useCallback((newOrder: string[]) => {
    setVisibleColumns(newOrder);
  }, []);

  // ── View mode ───────────────────────────────────────────────────────────────
  const rawView = searchParams.get('view');
  const viewMode: 'list' | 'table' = rawView === 'table' ? 'table' : 'list';
  const setViewMode = useCallback(
    (mode: 'list' | 'table') => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (mode === 'list') next.delete('view'); else next.set('view', mode);
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  // ── Filter panel open ───────────────────────────────────────────────────────
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const toggleFilterPanel = useCallback(() => setFilterPanelOpen(o => !o), []);

  // ── Row selection ───────────────────────────────────────────────────────────
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const toggleRowSelection = useCallback((id: string) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const selectAllRows = useCallback((ids: string[]) => setSelectedRows(new Set(ids)), []);
  const clearSelection = useCallback(() => setSelectedRows(new Set()), []);

  return {
    // Search
    searchQuery,
    setSearchQuery,
    // Sort
    sort,
    setSort,
    toggleSort,
    // Group
    groupBy,
    setGroupBy,
    isGroupOpen,
    toggleGroup,
    // Filters
    activeFilters,
    setFieldFilter,
    clearAllFilters,
    filterCount,
    applyFilters,
    filterFields,
    filterPanelOpen,
    toggleFilterPanel,
    // Density
    density,
    setDensity,
    // Columns
    visibleColumns,
    toggleColumn,
    reorderColumns,
    // View mode
    viewMode,
    setViewMode,
    // Selection
    selectedRows,
    toggleRowSelection,
    selectAllRows,
    clearSelection,
  };
}
